import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Link } from 'react-router';
import { FeatureNodeType } from '../../../data/features';

interface FeatureLinksProps {
  label: string;
  docsLink?: string;
  link?: string;
  nodeType?: FeatureNodeType;
}

export const FeatureLinks: React.FC<FeatureLinksProps> = ({
  label,
  docsLink,
  link,
  nodeType = FeatureNodeType.FEATURE
}) => {
  if (!docsLink && !link) return null;
  return (
    <Links>
      {[
        { to: link, text: 'Product' },
        { to: docsLink, text: 'Docs' }
      ].map(({ to, text }) =>
        to ? (
          <ResourceLink
            key={text}
            to={to}
            nodeType={nodeType}
            aria-label={`${label} ${text.toLowerCase()}`}
            title={text}
            data-feature-link
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {text === 'Docs' ? (
                <>
                  <path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8l-5-5Z" />
                  <path d="M14 3v5h5M9 12h6M9 16h6" />
                </>
              ) : (
                <>
                  <circle cx="12" cy="12" r="9" />
                  <path d="m16 8-2.5 5.5L8 16l2.5-5.5L16 8Z" />
                </>
              )}
            </svg>
            <span>{text}</span>
          </ResourceLink>
        ) : null
      )}
    </Links>
  );
};

const Links = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
  margin-left: auto;
`;

const ResourceLink = styled(Link)<{ nodeType: FeatureNodeType }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  white-space: nowrap;
  gap: 6px;
  padding: 0 6px;
  height: 32px;
  border-radius: 6px;
  color: ${(p) => p.theme.featureGraph[p.nodeType].muted};
  font-size: 12px;
  text-decoration: none;
  &:hover {
    color: ${(p) => p.theme.featureGraph[p.nodeType].accent};
    background: ${(p) => p.theme.featureGraph[p.nodeType].background};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.featureGraph[p.nodeType].muted};
    outline-offset: 2px;
  }
`;

export const featureLinksHover = css`
  @media (hover: hover) and (pointer: fine) {
    > [data-feature-header] [data-feature-link] {
      opacity: 0.4;
      transition: opacity 160ms ease;
    }
    &:hover > [data-feature-header] [data-feature-link],
    &:focus-within > [data-feature-header] [data-feature-link] {
      opacity: 1;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    > [data-feature-header] [data-feature-link] {
      transition: none;
    }
  }
`;
